import { GageLogo } from "./GageLogo.tsx";
import IconArrowUp from "tabler-icons/arrow-up.tsx";
import IconStop from "tabler-icons/player-stop.tsx";
import IconUpload from "tabler-icons/paperclip.tsx";
import IconSmart from "tabler-icons/brain.tsx";
import { useEffect, useMemo, useRef } from "preact/hooks";
import { useComputed, useSignal } from "@preact/signals";
import { fetchOrError, fetchStream } from "../app/utils.ts";
import { streamClientAI } from "../app/client-ai.ts";
import { useAppData } from "./App.tsx";
import { Messages } from "@/components/Messages.tsx";
import { Templates } from "@/components/Templates.tsx";
import { setURL } from "@/app/client-utils.ts";
import { FilesList } from "./FilesList.tsx";
import { useFileHandler } from "../hooks/useFileHandler.tsx";
import { type Doc, type Chat, type Message } from "db";

export default function Chat() {
  const hasText = useSignal(false);
  const mode = useSignal<number | undefined>(undefined);
  const controller = useSignal<AbortController | null>(null);
  const appData = useAppData();
  const generating = appData.generating;
  const messages = appData.messages;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagePage = useSignal(1); // Page position for loading messages
  const messageLastPage = useSignal<number | null>(null); // The last page of messages pagination
  const loadingMessages = useSignal(false);
  const isSmartMode = useSignal(false);
  const { files, uploadFile, getDragHandlers, DragBox } = useFileHandler();

  useEffect(() => {
    initChat();

    setTimeout(() => {
      setInterval(() => {
        checkLazyLoadMessages();
      }, 100);

      setInterval(() => {
        updateChatData();
      }, 2000);
    }, 500);
  }, []);

  async function updateChatData() {
    if (!appData.chatData.value?.uuid) return;
    const chatData = await fetchOrError<Chat>(`/api/chat-data/${appData.chatData.value.uuid}`);
    if (!chatData) return;
    appData.chatData.value = chatData;
  }

  useEffect(() => {
    const modeData = appData.modes.value?.find(m => m.id == mode.value);
    if (modeData) {
      isSmartMode.value = modeData.is_smart_mode === 1;
    }
  }, [mode.value])

  async function initChat() {
    mode.value = appData.chatData.value?.mode_id || appData.siteData.value?.default_mode_id || appData.modes.value[0]?.id;

    if (appData.isStartPage.value) return;
    messagePage.value = 1;
    messageLastPage.value = null;
    messages.value = [];
    appData.documents.value = [];
    await loadMessages();
    scrollToBottom(0, true);
  }

  async function loadMessages() {
    if (loadingMessages.value) return;
    if (!appData.chatData.value) return;
    if (messageLastPage.value && messagePage.value > messageLastPage.value) {
      // At last page
      return;
    }
    loadingMessages.value = true;

    const container = document.querySelector('.chat-ui-container')! as HTMLDivElement;
    const oldScrollHeight = container.scrollHeight;

    type Res = { messages: Message[], page: number, totalPages: number, total: string, documents: Doc[] };

    const res = await fetchOrError<Res>(`/api/messages?uuid=${appData.chatData.value.uuid}&page=${messagePage.value}`);

    messages.value = [...res.messages, ...messages.value];
    sortMessages();
    messagePage.value += 1;
    messageLastPage.value = res.totalPages;
    appData.documents.value = [...appData.documents.value, ...res.documents];
    console.log(appData.documents.value);

    container.style.scrollBehavior = 'auto';
    setTimeout(() => {
      const diff = container.scrollHeight - oldScrollHeight;
      container.scrollTo({ top: diff });
      setTimeout(() => {
        container.style.scrollBehavior = 'smooth';
      }, 0);
    }, 50)

    loadingMessages.value = false;
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key == "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function formClick(e: MouseEvent) {
    // Focus the textarea if we are selecting the input box, so its not a button
    if (e.target instanceof HTMLFormElement || e.target instanceof HTMLDivElement)
      textareaRef.current?.focus()
  }

  function clearInput() {
    if (textareaRef.current) {
      textareaRef.current.value = '';
      hasText.value = false;
    }
  }

  function focusInput() {
    textareaRef.current?.focus();
  }

  async function submit(e?: SubmitEvent) {
    e?.preventDefault();

    const prompt = textareaRef.current?.value;

    if (!prompt || generating.value) return;

    if (appData.isStartPage.value) await createChat();

    generateResponse({ prompt });
  }

  async function createChat() {
    const { uuid } = await fetchOrError<{ uuid: string }>('/api/chat-data', { method: "POST", body: { modeId: mode.value } });
    await loadChat(uuid);
  }

  async function loadChat(uuid: string) {
    setURL(`${appData.siteURL}/chat/` + uuid);
    const chatData = await fetchOrError<Chat>('/api/chat-data/' + uuid);
    appData.chatData.value = chatData;
    appData.isStartPage.value = false;
    scrollToBottom();
    initChat();
  }

  function sortMessages() {
    appData.messages.value = appData.messages.value.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }

  async function generateResponse({ prompt }: { prompt: string }) {
    generating.value = true;
    scrollToBottom(500);

    // We got the prompt, now we can clear the input, log just in case
    console.log({ prompt });
    clearInput();
    focusInput();

    const documents = files.value.map(f => f.filePath ?
      { title: f.name, filename: f.name, content: '', type: 'image', file_path: f.filePath } :
      { title: f.name, filename: f.name, content: f.text });

    files.value.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    files.value = [];

    controller.value = new AbortController();

    const stream = fetchStream(`/api/generate`, {
      method: 'post',
      body: JSON.stringify({
        uuid: appData.chatData.value?.uuid,
        prompt: prompt,
        documents: documents,
        isSmartMode: isSmartMode.value,
      }),
      signal: controller.value?.signal,
    });

    for await (const chunk of stream) {
      for (const line of chunk.split('\n')) {
        if (!line.trim()) continue;

        try {
          const { documents, messages } = JSON.parse(line) as { documents?: Doc[], messages?: Message[] };

          // TODO: method for setting these arrays in the same fashion

          // If we have documents, update/add them
          if (documents?.length) {
            const docMap = new Map<number, Doc>();
            appData.documents.value.forEach(d => docMap.set(d.id, d));
            documents.forEach(d => docMap.set(d.id, d));
            appData.documents.value = [...docMap.values()];
          }
          // If we have messages update/add them
          if (messages?.length) {
            const messagesMap = new Map<number, Message>();
            appData.messages.value.forEach(m => messagesMap.set(m.id, m));
            messages.forEach(m => messagesMap.set(m.id, m));

            let newMessages = [...messagesMap.values()];

            sortMessages();

            appData.messages.value = newMessages;
          }

          scrollToBottom(100);
        } catch (e) {
          console.error(e);
        }
      }
    }

    generating.value = false;
  }

  function scrollToBottom(dist = 0, instant = false) {
    const container = document.querySelector('.chat-ui-container')!;
    if (!container || dist && container.scrollTop <= container.scrollHeight - container.clientHeight - dist) return;

    const originalBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = instant ? 'auto' : 'smooth';

    setTimeout(() => {
      container.scrollTop = container.scrollHeight;
      // Restore the original behavior after scrolling
      setTimeout(() => {
        container.style.scrollBehavior = originalBehavior;
      }, 0);
    }, 0);
  }

  function stopResponse(e: Event) {
    e.preventDefault();
    e.stopPropagation();


    try {
      controller.value?.abort();
      messages.value.at(-1)!.thinking!.open = false;
    } finally {
      controller.value = null;
      messages.value = [...messages.value];
      generating.value = false;
    }
  }

  function onInput() {
    const value = textareaRef.current?.value;
    hasText.value = !!value;
  }

  function checkLazyLoadMessages() {
    const container = document.querySelector('.chat-ui-container')!;
    const position = container?.scrollTop;
    if (position < 300) loadMessages();
  }

  return (
    <div
      class="chat-ui-container"
      style={{ position: 'relative' }}
      {...getDragHandlers()}
    >
      <DragBox />
      <div class="chat-ui">
        {!appData.isStartPage.value ?
          <div class="messages" ref={scrollRef}>
            {/* {(messagePage.value <= messageLastPage.value!) && <button class="load-messages" onClick={loadMessages}>Load More</button>} */}
            <Messages />
          </div>
          :
          <h1 class="title">
            Welcome to <GageLogo /> Ignite<br />
            <span class="subtitle">How can I help you today, {appData.user.value?.name.split(" ").at(0)}?</span>
          </h1>
        }
        {!(appData.chatData.value && appData.chatData.value?.user_id != appData.user.value?.id) && <div className="input">
          <form onClick={formClick} onKeyDown={onKeyDown} onSubmit={submit}>
            <textarea rows={1}
              placeholder="Ask Gage Ignite" ref={textareaRef} autofocus autocomplete="off"
              onInput={onInput}
            ></textarea>
            <div class="toolbar">
              <div className="left">
                <button type="button" onClick={uploadFile} title="Upload file"><IconUpload /></button>
                <button type="button" class="smart-mode" onClick={() => isSmartMode.value = !isSmartMode.value} title="Advanced Mode" data-activated={isSmartMode.value}><IconSmart /><span class="title">Advanced Mode ×</span></button>
                <FilesList files={files} />
              </div>
              <div className="right">
                <select disabled={!appData.isStartPage.value} onChange={e => mode.value = +(e.target as HTMLSelectElement).value} value={mode.value}>
                  {appData.modes.value.map(mode =>
                    <option value={mode.id} key={mode.id}>{mode.name}</option>
                  )}
                </select>
                {!generating.value ?
                  <button type="submit" disabled={!hasText.value} title="Send"><IconArrowUp /></button>
                  :
                  <button type="button" onClick={stopResponse} title="Stop generation"><IconStop /></button>
                }
              </div>
            </div>
          </form>
          <Templates submit={submit} mode={mode} textareaRef={textareaRef} />
        </div>}
      </div>
    </div>
  )
}